import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Users, Radio, CalendarDays, CalendarRange, Send, ArrowUpRight, Plus, History, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { syncAllIndexNowPages } from "@/lib/indexnow.functions";
import { getAdminOverview } from "@/lib/admin-overview.functions";
import { OnlineMembersPanel } from "@/components/admin/OnlineMembersPanel";
import { ActivityTimeline } from "@/components/admin/ActivityTimeline";

export const Route = createFileRoute("/_authenticated/admin/")({
  head: () => ({ meta: [{ title: "Admin Overview | XAU Pro Academy" }, { name: "robots", content: "noindex" }] }),
  component: AdminHome,
});

function fmtR(r: number) {
  return `${r > 0 ? "+" : ""}${r.toFixed(2)}R`;
}

function AdminHome() {
  const { user, isHydrated } = useAuth();
  const fetchOverview = useServerFn(getAdminOverview);
  const q = useQuery({
    queryKey: ["admin", "overview", user?.id],
    queryFn: () => fetchOverview(),
    enabled: isHydrated && !!user,
    refetchInterval: 60_000,
  });
  const d = q.data;
  const dash = "\u2013";

  return (
    <div className="min-w-0 space-y-8 overflow-x-clip">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">Executive overview</p>
          <h1 className="mt-1 font-display text-3xl font-bold">Admin Desk</h1>
          <p className="mt-1 text-sm text-muted-foreground">Members, live positions and desk returns at a glance.</p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to="/admin/subscribers"><Users className="h-4 w-4" /> Subscribers</Link>
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          icon={<Users className="h-5 w-5" />}
          label="Registered members"
          value={d ? d.members.total : dash}
          to="/admin/subscribers"
          rows={[
            ["New today", d ? `+${d.members.today}` : dash],
            ["Last 7 days", d ? `+${d.members.week}` : dash],
            ["Paid / Telegram", d ? `${d.members.paid} / ${d.members.telegram}` : dash],
          ]}
        />
        <KpiCard
          icon={<Radio className="h-5 w-5" />}
          label="Open positions"
          value={d ? d.open : dash}
          to="/admin/otas"
          rows={[["Intraday / Swing", d ? `${d.openIntraday} / ${d.openSwing}` : dash], ["Opened this week", d ? String(d.week.opened) : dash], ["Opened this month", d ? String(d.month.opened) : dash]]}
        />
        <PeriodCard icon={<CalendarDays className="h-5 w-5" />} label="This week" p={d?.week} />
        <PeriodCard icon={<CalendarRange className="h-5 w-5" />} label="This month" p={d?.month} />
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <OnlineMembersPanel />
        <ActivityTimeline />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="orion-surface rounded-xl p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Signal operations</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Button asChild variant="outline" size="sm"><Link to="/admin/signals/new"><Plus className="h-4 w-4" /> New signal</Link></Button>
            <Button asChild variant="outline" size="sm"><Link to="/admin/signals"><ListChecks className="h-4 w-4" /> Manage signals</Link></Button>
            <Button asChild variant="outline" size="sm"><Link to="/admin/history"><History className="h-4 w-4" /> Trade history</Link></Button>
          </div>
        </div>
        <div className="orion-surface rounded-xl p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Search indexing</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <IndexNowButton />
            <IndexNowButton force />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Sync sends only new or updated pages to Bing, Yandex, Seznam and Naver. Force All resubmits every public page.
          </p>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, rows, to }: { icon: React.ReactNode; label: string; value: number | string; rows: [string, string][]; to?: string }) {
  return (
    <div className="orion-surface group relative flex flex-col rounded-xl p-5">
      <div className="flex items-center justify-between">
        <span className="rounded-md bg-gold/10 p-2 text-gold">{icon}</span>
        {to && (
          <Link to={to} className="text-muted-foreground transition-colors hover:text-gold" aria-label={`Open ${label}`}>
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        )}
      </div>
      <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
      <p className="mt-1 font-mono text-3xl font-semibold tabular-nums text-foreground">{value}</p>
      <dl className="mt-4 space-y-1.5 border-t border-border/40 pt-3 text-xs">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-2">
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="font-mono tabular-nums text-foreground">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function PeriodCard({ icon, label, p }: { icon: React.ReactNode; label: string; p?: { opened: number; closed: number; netR: number; wins: number; losses: number } }) {
  const dash = "\u2013";
  const tone = !p || p.netR === 0 ? "text-foreground" : p.netR > 0 ? "text-emerald" : "text-destructive";
  const acc = p && p.closed > 0 ? `${Math.round((p.wins / p.closed) * 100)}%` : dash;
  return (
    <div className="orion-surface flex flex-col rounded-xl p-5">
      <span className="w-fit rounded-md bg-gold/10 p-2 text-gold">{icon}</span>
      <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label} · net return</p>
      <p className={`mt-1 font-mono text-3xl font-semibold tabular-nums ${tone}`}>{p ? fmtR(p.netR) : dash}</p>
      <dl className="mt-4 space-y-1.5 border-t border-border/40 pt-3 text-xs">
        <div className="flex justify-between"><dt className="text-muted-foreground">Trades opened</dt><dd className="font-mono tabular-nums">{p ? p.opened : dash}</dd></div>
        <div className="flex justify-between"><dt className="text-muted-foreground">Closed (W / L)</dt><dd className="font-mono tabular-nums">{p ? `${p.closed} (${p.wins} / ${p.losses})` : dash}</dd></div>
        <div className="flex justify-between"><dt className="text-muted-foreground">Accuracy</dt><dd className="font-mono tabular-nums">{acc}</dd></div>
      </dl>
    </div>
  );
}

function IndexNowButton({ force = false }: { force?: boolean }) {
  const [loading, setLoading] = useState(false);
  const syncIndexNow = useServerFn(syncAllIndexNowPages);
  return (
    <Button
      variant={force ? "ghost" : "outline"}
      size="sm"
      disabled={loading}
      onClick={async () => {
        setLoading(true);
        try {
          const result = await syncIndexNow({ data: { force } });
          if (result.deferred) {
            toast.info(
              result.retryAfterS
                ? `All IndexNow gateways returned 429. Provider asks to retry in ${result.retryAfterS} seconds.`
                : "All IndexNow gateways returned 429. Pending pages stay queued for the next sync.",
            );
          } else if (!result.ok) {
            throw new Error(`Sync failed (HTTP ${result.status ?? "unknown"})`);
          } else if (result.submitted === 0) {
            toast.info(`No new or updated pages. ${result.total} pages already submitted.`);
          } else {
            toast.success(`IndexNow: ${result.submitted} of ${result.total} pages submitted (HTTP ${result.status ?? 200})`);
          }
        } catch (err) {
          toast.error(`IndexNow failed: ${(err as Error).message}`);
        } finally {
          setLoading(false);
        }
      }}
    >
      <Send className="h-4 w-4" />
      {loading ? "Submitting." : force ? "Force All" : "IndexNow Sync"}
    </Button>
  );
}
